import { BellOutlined, CheckOutlined, InboxOutlined } from "@ant-design/icons"
import { App, Badge, Button, Empty, Popover, Segmented, Tooltip } from "antd"
import { useCallback, useMemo, useState } from "react"
import styled from "styled-components"

import Counter from "#blocks/Counter"
import Flex from "#blocks/Flex"
import LinkButton from "#blocks/LinkButton"
import { useUserContext } from "#contexts/UserContextProvider"
import type { NotificationConstructor } from "#customTypes/composedTypes/notificationTypes"
import {
  NOTIFICATION_SUBTYPE,
  NOTIFICATION_TYPE,
} from "#customTypes/composedTypes/notificationTypes"
import { Uuid } from "#customTypes/globalTypes"
import {
  useArchivedNotifications,
  useNotificationActions,
  useNotificationToastEffect,
  useUserNotifications,
} from "#hooks/jotai/useNotifications"

import PortfolioManagementModal from "../PortfolioManagement/PortfolioManagementModal"
import EvaluationFormModal from "../views/EvaluationView/EvaluationFormModal"
import { EvaluationFormContextProvider } from "../views/EvaluationView/useEvaluationFormContext"

import ChangelogModal from "./ChangelogModal"
import NotificationItem from "./NotificationItem"

type Tab = "Unread" | "All" | "Archived"

const SPopoverContent = styled.div`
  width: 380px;
  max-height: 480px;
  overflow-y: auto;
`

const SPopoverHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
`

const NotificationCenter = () => {
  const { currentUserId } = useUserContext()
  const { notifications, unreadCount } = useUserNotifications(currentUserId)
  const archivedNotifications = useArchivedNotifications(currentUserId)
  const { toggleRead, markAllAsRead, archiveNotification, archiveAll } =
    useNotificationActions()

  const [popoverOpen, setPopoverOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<Tab>("Unread")

  // Lazy modal state
  const [activeEval, setActiveEval] = useState<{
    id: Uuid
    defaultTab?: string
  } | null>(null)
  const [activePortfolio, setActivePortfolio] = useState<{
    studentId: Uuid
    projectId: string
  } | null>(null)
  const [changelogContent, setChangelogContent] = useState<{
    items: string[]
    version?: string
  } | null>(null)

  // Toast notifications for new arrivals
  const { notification: antdNotification } = App.useApp()

  const activateNotification = useCallback(
    (n: NotificationConstructor) => {
      if (!n.read) {
        void toggleRead(n)
      }

      switch (n.type) {
        case NOTIFICATION_TYPE.EVALUATION: {
          const evalId = n.metadata?.evaluationId as Uuid | undefined
          if (evalId) {
            const defaultTab =
              n.subtype === NOTIFICATION_SUBTYPE.EVAL_COMMENT
                ? "comments"
                : undefined
            setActiveEval({ id: evalId, defaultTab })
          }
          break
        }
        case NOTIFICATION_TYPE.PORTFOLIO: {
          const studentId = n.metadata?.studentId as Uuid | undefined
          const projectId = n.metadata?.projectId as string | undefined
          if (studentId && projectId) {
            setActivePortfolio({ studentId, projectId })
          }
          break
        }
        case NOTIFICATION_TYPE.CHANGELOG: {
          const items = n.metadata?.changelogItems as string[] | undefined
          const version = n.metadata?.changelogVersion as string | undefined
          setChangelogContent({
            items: items ?? [],
            version,
          })
          break
        }
      }
    },
    [toggleRead]
  )

  const showToast = useCallback(
    (n: NotificationConstructor) => {
      antdNotification.info({
        key: n.id,
        message: n.header,
        description: (
          <Flex col gap={4}>
            <span>{n.message}</span>
            <LinkButton
              style={{ fontSize: 12, padding: 0 }}
              onClick={(e) => {
                e.stopPropagation()
                antdNotification.destroy(n.id)
                activateNotification(n)
              }}
            >
              Click to view
            </LinkButton>
          </Flex>
        ),
        placement: "topRight",
        duration: 3,
        onClick: () => {
          antdNotification.destroy(n.id)
          activateNotification(n)
        },
      })
    },
    [antdNotification, activateNotification]
  )

  useNotificationToastEffect(notifications, showToast)

  const displayList = useMemo(() => {
    switch (activeTab) {
      case "Unread":
        return notifications.filter((n) => !n.read)
      case "All":
        return notifications
      case "Archived":
        return archivedNotifications
    }
  }, [activeTab, notifications, archivedNotifications])

  const handleClickBody = useCallback(
    (n: NotificationConstructor) => {
      setPopoverOpen(false)
      activateNotification(n)
    },
    [activateNotification]
  )

  const handleMarkAllRead = useCallback(() => {
    void markAllAsRead(notifications)
  }, [markAllAsRead, notifications])

  const handleArchiveAll = useCallback(() => {
    void archiveAll(notifications)
  }, [archiveAll, notifications])

  const popoverContent = (
    <SPopoverContent>
      <SPopoverHeader>
        <Segmented
          size="small"
          options={["Unread", "All", "Archived"] as Tab[]}
          value={activeTab}
          onChange={(val) => setActiveTab(val as Tab)}
        />
        <Flex gap={4}>
          {activeTab !== "Archived" && (
            <>
              <Tooltip title="Mark all read">
                <Button
                  type="text"
                  size="small"
                  icon={<CheckOutlined />}
                  onClick={handleMarkAllRead}
                  disabled={unreadCount === 0}
                />
              </Tooltip>
              <Tooltip title="Archive all">
                <Button
                  type="text"
                  size="small"
                  icon={<InboxOutlined />}
                  onClick={handleArchiveAll}
                  disabled={notifications.length === 0}
                />
              </Tooltip>
            </>
          )}
        </Flex>
      </SPopoverHeader>

      {displayList.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            activeTab === "Unread"
              ? "No unread notifications"
              : activeTab === "Archived"
                ? "No archived notifications"
                : "No notifications"
          }
          style={{ padding: "32px 0" }}
        />
      ) : (
        displayList.map((n) => (
          <NotificationItem
            key={n.id}
            notification={n}
            onClickBody={handleClickBody}
            onToggleRead={toggleRead}
            onArchive={archiveNotification}
          />
        ))
      )}
    </SPopoverContent>
  )

  return (
    <>
      <Popover
        content={popoverContent}
        trigger="click"
        placement="bottomRight"
        open={popoverOpen}
        onOpenChange={setPopoverOpen}
        arrow={false}
        overlayInnerStyle={{ padding: 0 }}
      >
        <Badge
          count={
            unreadCount > 0 ? (
              <span
                style={{
                  background: "#ff4d4f",
                  borderRadius: 10,
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 600,
                  minWidth: 18,
                  height: 18,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0 5px",
                  boxShadow: "0 0 0 1px #fff",
                }}
              >
                <Counter
                  value={unreadCount}
                  style={{ fontSize: 11, color: "#fff", lineHeight: 1 }}
                />
              </span>
            ) : (
              0
            )
          }
          offset={[-4, 4]}
        >
          <BellOutlined
            style={{
              fontSize: 20,
              color: "#fff",
              cursor: "pointer",
            }}
          />
        </Badge>
      </Popover>

      {activeEval && (
        <EvaluationFormContextProvider>
          <EvaluationFormModal
            evaluationId={activeEval.id}
            defaultTab={activeEval.defaultTab}
            onClose={() => setActiveEval(null)}
          />
        </EvaluationFormContextProvider>
      )}
      {activePortfolio && (
        <PortfolioManagementModal
          isOpen
          handleCloseAttempt={() => setActivePortfolio(null)}
          title="Portfolio management"
          initialValues={{ id: activePortfolio.studentId }}
          initialProjectId={activePortfolio.projectId}
        />
      )}
      {changelogContent !== null && (
        <ChangelogModal
          onClose={() => setChangelogContent(null)}
          changelogItems={changelogContent.items}
          changelogVersion={changelogContent.version}
        />
      )}
    </>
  )
}

export default NotificationCenter
